import { Component, Input } from '@angular/core';
import { ClientPendingDetailSubProject } from '../../../../model/project/client-pending-detail-sub-project';
import { SubProjectControllerRequestService } from '../../../../services/api/sub-project-controller-request.service';
import DateHelper from '../../../../helpers/date-helper';
import { OnView } from 'src/app/model/tabs/on-view';
import Asset from 'src/app/model/asset';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import Company from 'src/app/model/company/company';
import { ObservableHelper } from 'src/app/helpers/observable-helper';
import { OnViewUpdatable } from 'src/app/model/tabs/on-view-updatable/on-view-updatable';
import {
  NotExecutedHistoryItemAssetsFetchedEvent,
  ProjectDisplayObservableService,
} from 'src/app/services/project-display-observable.service';
import ProductHelper from '../../../../helpers/product-helper';
import { formatFirstProduct } from '../../../projects/helpers/first-product/first-product-helper';

@Component({
  selector: 'client-pending-detail',
  templateUrl: './client-pending-detail.component.html',
  styleUrls: ['./client-pending-detail.component.scss'],
})
export class ClientPendingDetailComponent
  extends SubscriptionBasedComponent
  implements OnView<string>
{
  @Input()
  subProjectId: number;

  @Input()
  forAtelier: boolean = false;

  detail: ClientPendingDetailSubProject;
  placementAssets: Asset[] = [];
  ipBoardExamples: Asset[] = [];
  executionAssets: Asset[] = [];
  viewed: boolean = false;
  loading: boolean = false;

  selectedCompany: Company;

  onViewUpdatable: OnViewUpdatable<string>;

  constructor(
    private subProjectService: SubProjectControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private clientCompanySelectService: ClientCompanySelectService
  ) {
    super();
  }

  onView(onViewUpdatable?: OnViewUpdatable<string>): void {
    this.viewed = true;

    this.onViewUpdatable = onViewUpdatable;

    if (this.detail == null) {
      this.fetchDetail();
      ObservableHelper.untilFirstNonNull(
        this.clientCompanySelectService.getSelectionChangedSubscribable()
      )
        .toPromise()
        .then((company) => (this.selectedCompany = company));
    }
  }

  private fetchDetail(): void {
    this.loading = true;

    this.subProjectService
      .getClientPendingDetail(this.subProjectId)
      .then((res) => {
        if (
          res.project != null &&
          res.project.oldPlatformId != null &&
          this.onViewUpdatable != null
        ) {
          this.onViewUpdatable.onViewUpdate(
            res.project.oldPlatformId.toString()
          );
        }
        this.detail = this.detailUnpacked(res);
        this.detail.projectPlacement.placementPictures.forEach((a) => {
          if (!a.archived && a.formSection === 'sitePhotos') {
            this.placementAssets.push(a.asset);
          }
        });
        if (this.detail.componentsExecuted !== false) {
          this.executionAssets = this.detail.executionPictures;
        }
        if (this.detail.componentsExecuted === false) {
          this.addSubscription(
            this.projectDisplayObservableService.getNotExecutedHistoryItemAssetsFetchedSubscribable(),
            (event: NotExecutedHistoryItemAssetsFetchedEvent) => {
              if (
                event.subProjectId === this.detail.id &&
                event.assets != null
              ) {
                this.executionAssets = event.assets.map((a) => a.asset);
              }
            }
          );
        }
      })
      .catch((error) => console.error(error))
      .finally(() => (this.loading = false));
  }

  private detailUnpacked(
    subproject: ClientPendingDetailSubProject
  ): ClientPendingDetailSubProject {
    if (subproject.currentConfirmedPlacementDate != null) {
      subproject.currentConfirmedPlacementDateDisplay = DateHelper.formatDate(
        subproject.currentConfirmedPlacementDate
      );
    }
    if (
      subproject.executionPictures != null &&
      subproject.executionPictures.length > 0
    ) {
      subproject.firstExecutionPicture = subproject.executionPictures[0];
    }
    if (
      subproject.designPictures != null &&
      subproject.designPictures.length > 0
    ) {
      subproject.firstDesignPicture = subproject.designPictures[0];
    }

    this.initProjectFields(subproject);

    if (subproject.projectPlacement != null) {
      if (
        subproject.projectPlacement.placementPictures != null &&
        subproject.projectPlacement.placementPictures.length > 0
      ) {
        subproject.projectPlacement.firstPlacementPicture =
          subproject.projectPlacement.placementPictures[0];
      }
    }

    return subproject;
  }

  private initProjectFields(subproject: ClientPendingDetailSubProject): void {
    if (subproject.project != null) {
      if (
        subproject.project.products != null &&
        subproject.project.products.length > 0
      ) {
        subproject.project.firstProduct = subproject.project.products[0];
        const ipBoardExamples = [
          subproject.project.firstProduct.ipBoardLeftExample,
          subproject.project.firstProduct.ipBoardRightExample,
        ];
        CollectionHelper.filterInPlace(ipBoardExamples, (e) => e != null);
        if (
          ipBoardExamples.length === 2 &&
          ipBoardExamples[0].uuid === ipBoardExamples[1].uuid
        ) {
          ipBoardExamples.length = 1;
        }
        this.ipBoardExamples = ipBoardExamples;
        if (
          ProductHelper.isIPBoard(subproject.project.products[0]) &&
          subproject.project.products[0].boardPart != null
        ) {
          subproject.project.products[0] = formatFirstProduct(
            subproject.project.products[0],
            false
          );
        }
      }
    }
  }
}
